import { SelectProps } from 'antd';
import InfiniteSelect from '../InfiniteSelect';
import {
  useBenefitConsumableDetailMutation,
  useBenefitConsumableList,
} from '@/core/domain/operational/benefits-consumable/benefits-consumable.hooks';
import useSelectOptions from '@/hooks/useSelectOptions';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useState, useEffect } from 'react';
import { useDebounce } from '@/hooks';
import {
  BenefitConsumable,
  BenefitConsumableListRequest,
} from '@/core/domain/operational/benefits-consumable/benefits-consumable.types';

type Props = Omit<SelectProps, 'options'> & {
  queryFilter?: BenefitConsumableListRequest;
  queryEnable?: boolean;
  onConsumableSelected?: (consumable?: BenefitConsumable) => void;
  disableValues?: number[];
};

export default function BenefitConsumableSelect({
  queryFilter,
  queryEnable = false,
  disableValues,
  ...props
}: Props) {
  const [selectClicked, setSelectClicked] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>();
  const search = useDebounce(searchValue);

  const consumableList = useBenefitConsumableList(
    {
      ...queryFilter,
      rows: 10,
      search,
    },
    (queryEnable || selectClicked) && !props.disabled,
  );

  const consumables = flatInfiniteData(consumableList.data);

  const consumableDetail = useBenefitConsumableDetailMutation();

  const selectOptions = useSelectOptions({
    list: consumableList,
    detail: consumableDetail,
    selectProps: props,
    labelFormatter: (consumable) =>
      `${consumable.consumableCode} • ${consumable.name}`,
  });

  const options = selectOptions.map((option) => ({
    ...option,
    disabled: disableValues?.includes(option.value as number),
  }));

  useEffect(() => {
    if (!props.value && !!consumableDetail.data?.id) {
      consumableDetail.reset();
    } else if (
      consumableDetail.data?.id &&
      consumableDetail.data.id === props.value &&
      !!props.onConsumableSelected
    ) {
      props.onConsumableSelected(consumableDetail.data);
    }
  }, [consumableDetail.data, props.value]);

  return (
    <InfiniteSelect
      fetchNextPage={consumableList.fetchNextPage}
      hasNextPage={consumableList.hasNextPage}
      loading={consumableList.isFetching}
      options={options}
      onSelect={(consumableId: number) => {
        setSelectClicked(false);

        const selectedConsumable = consumables?.find(
          (consumable) => consumable.id === consumableId,
        );

        if (!!props.onConsumableSelected) {
          props.onConsumableSelected(selectedConsumable);
        }
      }}
      onDropdownVisibleChange={(open) => setSelectClicked(open)}
      showSearch
      searchValue={searchValue}
      filterOption={false}
      onSearch={setSearchValue}
      {...props}
      onChange={(value, option) => {
        if (!!props.onConsumableSelected && !value) {
          props.onConsumableSelected(undefined);
        }
        if (props.onChange) {
          props.onChange(value, option);
        }
      }}
    />
  );
}
